import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { Badge } from '../components/SubscriptionAppBadge';
import { Button } from '../components/SubscriptionAppButton';
import { StatCard, TrendIndicator } from '../components/StatCard';
import { SearchBar } from '../components/SearchBar';
import { TableFilter } from '../components/TableFilter';
import type { Filter } from '../components/TableFilter';
import { ExportButton } from '../components/ExportButton';
import { ClipboardList, X, Copy, ChevronDown, ChevronUp, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { useActivities, customerNames } from '../contexts/ActivityContext';
import { useTasks } from '../contexts/TaskContext';
// Mock data for activity log
const mockActivityLog = [{
  id: '1',
  timestamp: '2026-01-07T14:34:00',
  eventType: 'Subscription',
  activity: 'Subscription paused - Reason: Going on vacation',
  entity: 'SUB-23144',
  entityType: 'subscription',
  source: 'Customer Portal',
  user: 'sarah.j@example.com',
  status: 'Success',
  ipAddress: '192.168.1.1',
  changes: [{
    field: 'Status',
    before: 'Active',
    after: 'Paused'
  }, {
    field: 'Pause Reason',
    before: '—',
    after: 'Going on vacation'
  }]
}, {
  id: 'auto-1',
  timestamp: '2026-01-07T14:31:00',
  eventType: 'Automation',
  activity: 'Automated Tag Added — "Needs Attention" applied by rule "At-Risk Member"',
  entity: 'SUB-23144',
  entityType: 'subscription',
  source: 'System',
  user: 'Automation: At-Risk Member',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tag',
    before: '—',
    after: 'Needs Attention'
  }, {
    field: 'Rule',
    before: '—',
    after: 'At-Risk Member'
  }]
}, {
  id: '11',
  timestamp: '2026-01-07T14:28:00',
  eventType: 'Loyalty',
  activity: 'Points earned - 120 points from subscription payment',
  entity: 'MEM-1042',
  entityType: 'member',
  source: 'System',
  user: 'Automated',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Points Balance',
    before: '2,330',
    after: '2,450'
  }]
}, {
  id: '2',
  timestamp: '2026-01-07T14:15:00',
  eventType: 'Payment',
  activity: 'Payment retry attempt 2/3 - Card declined',
  entity: 'SUB-23138',
  entityType: 'subscription',
  source: 'System',
  user: 'Automated',
  status: 'Failed',
  ipAddress: null,
  changes: [{
    field: 'Retry Count',
    before: '1',
    after: '2'
  }, {
    field: 'Last Retry',
    before: 'Jan 5, 2026',
    after: 'Jan 7, 2026'
  }]
}, {
  id: 'auto-2',
  timestamp: '2026-01-07T14:08:00',
  eventType: 'Automation',
  activity: 'Automated Tag Removed — "New Customer" removed by rule "First-Time Member" (exit criteria met)',
  entity: 'SUB-23141',
  entityType: 'subscription',
  source: 'System',
  user: 'Automation: First-Time Member',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tag',
    before: 'New Customer',
    after: '—'
  }, {
    field: 'Rule',
    before: '—',
    after: 'First-Time Member (exit criteria)'
  }]
}, {
  id: '12',
  timestamp: '2026-01-07T14:02:00',
  eventType: 'Tier',
  activity: 'Tier upgraded - Enthusiast to Connoisseur (automatic)',
  entity: 'MEM-1087',
  entityType: 'member',
  source: 'System',
  user: 'Automated',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tier',
    before: 'Enthusiast',
    after: 'Connoisseur'
  }, {
    field: 'Trigger',
    before: '—',
    after: 'Points threshold reached (500)'
  }]
}, {
  id: '3',
  timestamp: '2026-01-07T13:52:00',
  eventType: 'Order',
  activity: 'Subscription order created - 6 items, $126.50',
  entity: 'ORD-1042',
  entityType: 'order',
  source: 'System',
  user: 'Automated',
  status: 'Success',
  ipAddress: null,
  changes: []
}, {
  id: 'auto-3',
  timestamp: '2026-01-07T13:48:00',
  eventType: 'Automation',
  activity: 'Automated Tag Added — "Loyal" applied by rule "Loyal Member (12+ months)"',
  entity: 'SUB-23141',
  entityType: 'subscription',
  source: 'System',
  user: 'Automation: Loyal Member (12+ months)',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tag',
    before: '—',
    after: 'Loyal'
  }, {
    field: 'Rule',
    before: '—',
    after: 'Loyal Member (12+ months)'
  }]
}, {
  id: '13',
  timestamp: '2026-01-07T13:45:00',
  eventType: 'Rewards',
  activity: 'Reward redeemed - Free shipping (250 points)',
  entity: 'MEM-1042',
  entityType: 'member',
  source: 'Customer Portal',
  user: 'sarah.j@example.com',
  status: 'Success',
  ipAddress: '192.168.1.1',
  changes: [{
    field: 'Points Balance',
    before: '2,700',
    after: '2,450'
  }, {
    field: 'Reward',
    before: '—',
    after: 'Free shipping applied to next order'
  }]
}, {
  id: '4',
  timestamp: '2026-01-07T13:30:00',
  eventType: 'Subscription',
  activity: 'Products swapped - Shiraz 2022 → Shiraz 2023',
  entity: 'SUB-23141',
  entityType: 'subscription',
  source: 'Admin Portal',
  user: 'mike@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.5',
  changes: [{
    field: 'Product',
    before: 'Shiraz 2022',
    after: 'Shiraz 2023'
  }]
}, {
  id: '14',
  timestamp: '2026-01-07T13:10:00',
  eventType: 'Loyalty',
  activity: 'Points adjusted - +500 points (Cellar door visit)',
  entity: 'MEM-1234',
  entityType: 'member',
  source: 'Admin Portal',
  user: 'mike@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.5',
  changes: [{
    field: 'Points Balance',
    before: '350',
    after: '850'
  }, {
    field: 'Reason',
    before: '—',
    after: 'Cellar door visit'
  }]
}, {
  id: 'auto-4',
  timestamp: '2026-01-07T12:55:00',
  eventType: 'Automation',
  activity: 'Automated Tag Added — "High Value" applied by rule "High Value Spender"',
  entity: 'SUB-23138',
  entityType: 'subscription',
  source: 'System',
  user: 'Automation: High Value Spender',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tag',
    before: '—',
    after: 'High Value'
  }, {
    field: 'Rule',
    before: '—',
    after: 'High Value Spender'
  }]
}, {
  id: '5',
  timestamp: '2026-01-07T12:45:00',
  eventType: 'Cancellation',
  activity: 'Subscription cancelled - Reason: Too expensive, Retention offer: Declined 15% discount',
  entity: 'SUB-23135',
  entityType: 'subscription',
  source: 'Customer Portal',
  user: 'emma.w@example.com',
  status: 'Success',
  ipAddress: '192.168.1.10',
  changes: [{
    field: 'Status',
    before: 'Active',
    after: 'Cancelled'
  }, {
    field: 'Cancellation Reason',
    before: '—',
    after: 'Too expensive'
  }, {
    field: 'Retention Offer',
    before: '—',
    after: 'Declined 15% discount'
  }]
}, {
  id: 'auto-5',
  timestamp: '2026-01-07T12:30:00',
  eventType: 'Automation',
  activity: 'Automated Tag Removed — "Loyal" removed by rule "Loyal Member (12+ months)" (member cancelled)',
  entity: 'SUB-23135',
  entityType: 'subscription',
  source: 'System',
  user: 'Automation: Loyal Member (12+ months)',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tag',
    before: 'Loyal',
    after: '—'
  }, {
    field: 'Rule',
    before: '—',
    after: 'Loyal Member (12+ months) (exit criteria)'
  }]
}, {
  id: '15',
  timestamp: '2026-01-07T12:20:00',
  eventType: 'Loyalty',
  activity: 'Bulk adjustment - 500 points added to 8 members (Wine club event)',
  entity: '8 members',
  entityType: 'bulk-members',
  source: 'Admin Portal',
  user: 'chris@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.8',
  changes: [{
    field: 'Action',
    before: '—',
    after: '+500 points to 8 members'
  }, {
    field: 'Reason',
    before: '—',
    after: 'Wine club event'
  }]
}, {
  id: '16',
  timestamp: '2026-01-07T11:55:00',
  eventType: 'Tier',
  activity: 'Tier manually changed - Set to Vintner by admin',
  entity: 'MEM-1004',
  entityType: 'member',
  source: 'Admin Portal',
  user: 'mike@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.5',
  changes: [{
    field: 'Tier',
    before: 'Sommelier',
    after: 'Vintner'
  }, {
    field: 'Reason',
    before: '—',
    after: 'Goodwill gesture'
  }]
}, {
  id: '6',
  timestamp: '2026-01-07T11:20:00',
  eventType: 'Settings',
  activity: 'Payment recovery settings updated - Retry attempts changed from 3 to 4',
  entity: null,
  entityType: null,
  source: 'Admin Portal',
  user: 'chris@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.8',
  changes: [{
    field: 'Retry Attempts',
    before: '3',
    after: '4'
  }]
}, {
  id: 'auto-6',
  timestamp: '2026-01-07T11:10:00',
  eventType: 'Automation',
  activity: 'Automated Tag Added — "Premium" applied by rule "Red Wine Lover"',
  entity: 'SUB-23150',
  entityType: 'subscription',
  source: 'System',
  user: 'Automation: Red Wine Lover',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tag',
    before: '—',
    after: 'Premium'
  }, {
    field: 'Rule',
    before: '—',
    after: 'Red Wine Lover'
  }]
}, {
  id: '17',
  timestamp: '2026-01-07T11:05:00',
  eventType: 'Rewards',
  activity: 'Reward redeemed - 15% discount code (1,500 points)',
  entity: 'MEM-1004',
  entityType: 'member',
  source: 'Customer Portal',
  user: 'david.p@example.com',
  status: 'Success',
  ipAddress: '192.168.1.30',
  changes: [{
    field: 'Points Balance',
    before: '17,100',
    after: '15,600'
  }, {
    field: 'Discount Code',
    before: '—',
    after: 'LOYAL15-DPK-0107'
  }]
}, {
  id: '18',
  timestamp: '2026-01-07T10:40:00',
  eventType: 'Tier',
  activity: 'Tier locked - Prevented automatic recalculation',
  entity: 'MEM-1004',
  entityType: 'member',
  source: 'Admin Portal',
  user: 'mike@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.5',
  changes: [{
    field: 'Tier Lock',
    before: 'Unlocked',
    after: 'Locked'
  }]
}, {
  id: 'auto-7',
  timestamp: '2026-01-07T10:22:00',
  eventType: 'Automation',
  activity: 'Automated Tag Added — "VIP" applied by rule "Anniversary Approaching"',
  entity: 'SUB-23130',
  entityType: 'subscription',
  source: 'System',
  user: 'Automation: Anniversary Approaching',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tag',
    before: '—',
    after: 'VIP'
  }, {
    field: 'Rule',
    before: '—',
    after: 'Anniversary Approaching'
  }]
}, {
  id: '7',
  timestamp: '2026-01-07T10:05:00',
  eventType: 'Payment',
  activity: 'Payment recovered - $189.50 after retry 2',
  entity: 'SUB-23130',
  entityType: 'subscription',
  source: 'System',
  user: 'Automated',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Payment Status',
    before: 'Failed',
    after: 'Paid'
  }, {
    field: 'Amount Recovered',
    before: '$0.00',
    after: '$189.50'
  }]
}, {
  id: '19',
  timestamp: '2026-01-07T09:50:00',
  eventType: 'Loyalty',
  activity: 'Points reset - Balance set to 0 (Program migration)',
  entity: 'MEM-1008',
  entityType: 'member',
  source: 'Admin Portal',
  user: 'chris@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.8',
  changes: [{
    field: 'Points Balance',
    before: '50',
    after: '0'
  }, {
    field: 'Reason',
    before: '—',
    after: 'Program migration'
  }]
}, {
  id: '8',
  timestamp: '2026-01-07T09:30:00',
  eventType: 'System',
  activity: 'Daily subscription processing completed - 12 orders created',
  entity: null,
  entityType: null,
  source: 'System',
  user: 'Automated',
  status: 'Success',
  ipAddress: null,
  changes: []
}, {
  id: 'auto-8',
  timestamp: '2026-01-07T09:25:00',
  eventType: 'Automation',
  activity: 'Automated Tag Added — "New Customer" applied by rule "First-Time Member" (3 members matched)',
  entity: '3 members',
  entityType: 'bulk-members',
  source: 'System',
  user: 'Automation: First-Time Member',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tag',
    before: '—',
    after: 'New Customer'
  }, {
    field: 'Members Matched',
    before: '—',
    after: '3 new subscribers'
  }]
}, {
  id: '20',
  timestamp: '2026-01-07T09:15:00',
  eventType: 'Tier',
  activity: 'Bulk tier change - 5 members moved to Sommelier',
  entity: '5 members',
  entityType: 'bulk-members',
  source: 'Admin Portal',
  user: 'mike@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.5',
  changes: [{
    field: 'Action',
    before: '—',
    after: '5 members moved to Sommelier'
  }, {
    field: 'Reason',
    before: '—',
    after: 'Promotional campaign'
  }]
}, {
  id: '21',
  timestamp: '2026-01-07T08:50:00',
  eventType: 'Rewards',
  activity: 'Reward created - Birthday wine bottle added to catalog',
  entity: null,
  entityType: null,
  source: 'Admin Portal',
  user: 'chris@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.8',
  changes: []
}, {
  id: '9',
  timestamp: '2026-01-07T08:15:00',
  eventType: 'Subscription',
  activity: 'Subscription created - Monthly wine club',
  entity: 'SUB-23150',
  entityType: 'subscription',
  source: 'Customer Portal',
  user: 'john.d@example.com',
  status: 'Success',
  ipAddress: '192.168.1.20',
  changes: []
}, {
  id: '22',
  timestamp: '2026-01-07T08:00:00',
  eventType: 'Loyalty',
  activity: 'Points expired - 200 points expired',
  entity: 'MEM-1055',
  entityType: 'member',
  source: 'System',
  user: 'Automated',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Points Balance',
    before: '320',
    after: '120'
  }, {
    field: 'Expired Points',
    before: '—',
    after: '200'
  }]
}, {
  id: '10',
  timestamp: '2026-01-07T07:45:00',
  eventType: 'Payment',
  activity: 'Payment failed - Insufficient funds',
  entity: 'SUB-23145',
  entityType: 'subscription',
  source: 'System',
  user: 'Automated',
  status: 'Failed',
  ipAddress: null,
  changes: [{
    field: 'Payment Status',
    before: 'Pending',
    after: 'Failed'
  }, {
    field: 'Failure Reason',
    before: '—',
    after: 'Insufficient funds'
  }]
}, {
  id: '23',
  timestamp: '2026-01-07T07:30:00',
  eventType: 'Rewards',
  activity: 'Reward deactivated - Summer tasting experience',
  entity: null,
  entityType: null,
  source: 'Admin Portal',
  user: 'mike@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.5',
  changes: [{
    field: 'Reward Status',
    before: 'Active',
    after: 'Deactivated'
  }]
}, {
  id: '24',
  timestamp: '2026-01-07T07:10:00',
  eventType: 'Tier',
  activity: 'Tier downgraded - Sommelier to Connoisseur (automatic)',
  entity: 'MEM-1055',
  entityType: 'member',
  source: 'System',
  user: 'Automated',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tier',
    before: 'Sommelier',
    after: 'Connoisseur'
  }, {
    field: 'Trigger',
    before: '—',
    after: 'Points fell below threshold (2,000)'
  }]
}];
export function OperationsActivityLogPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    getAllNotes
  } = useActivities();
  const {
    getTaskActivityLogEvents
  } = useTasks();
  // Convert ActivityContext notes to activity log format
  const noteEvents = useMemo(() => {
    const allNotes = getAllNotes();
    return allNotes.map((note, index) => {
      const customerName = customerNames[note.customerId] || 'Unknown Customer';
      // Convert date string to ISO timestamp for consistent sorting
      const noteDate = new Date(note.date);
      // Add some time offset based on index to differentiate timestamps
      noteDate.setHours(12, 30 - index, 0, 0);
      return {
        id: `note-${note.id}`,
        timestamp: noteDate.toISOString(),
        eventType: 'Customer Notes',
        activity: `Note added on ${customerName} — "${note.description}"`,
        entity: customerName,
        entityType: 'contact' as string | null,
        source: 'Admin Portal',
        user: note.author || 'Admin',
        status: 'Success',
        ipAddress: null as string | null,
        changes: [] as {
          field: string;
          before: string;
          after: string;
        }[]
      };
    });
  }, [getAllNotes]);
  // Get task events from TaskContext
  const taskEvents = useMemo(() => {
    return getTaskActivityLogEvents();
  }, [getTaskActivityLogEvents]);
  // Merge mock data with live notes and task events
  const allActivityEvents = useMemo(() => {
    return [...mockActivityLog, ...noteEvents, ...taskEvents].sort((a, b) => {
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });
  }, [noteEvents, taskEvents]);
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedEvent, setSelectedEvent] = useState<(typeof allActivityEvents)[0] | null>(null);
  const [rawDataExpanded, setRawDataExpanded] = useState(false);
  const pageSize = 25;
  // Sort state
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({
    key: 'timestamp',
    direction: 'desc'
  });
  // Filter state
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({
    timestamp: [],
    eventType: [],
    activity: [],
    entity: [],
    source: [],
    user: [],
    status: [],
    date: []
  });
  const [addedFilters, setAddedFilters] = useState<Set<string>>(new Set());
  const [openFilterDropdowns, setOpenFilterDropdowns] = useState<Set<string>>(new Set());
  const [customDateRanges, setCustomDateRanges] = useState<Record<string, {
    startDate: string;
    endDate: string;
  }>>({});
  // Apply pre-filters from navigation state (e.g., from Dashboard "View All" button)
  useEffect(() => {
    const state = location.state as {
      filterByNotes?: boolean;
    } | null;
    if (state?.filterByNotes) {
      setFilterValues(prev => ({
        ...prev,
        eventType: ['customer notes']
      }));
      setAddedFilters(new Set(['eventType']));
      // Clear the state so refreshing doesn't re-apply
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  // Format date and time
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  // Calculate stats
  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventsToday = allActivityEvents.filter(event => {
      const eventDate = new Date(event.timestamp);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate.getTime() === today.getTime();
    }).length;
    const subscriptionChanges = allActivityEvents.filter(e => e.eventType === 'Subscription' || e.eventType === 'Cancellation').length;
    const paymentEvents = allActivityEvents.filter(e => e.eventType === 'Payment').length;
    const paymentFailed = allActivityEvents.filter(e => e.eventType === 'Payment' && e.status === 'Failed').length;
    const loyaltyEvents = allActivityEvents.filter(e => e.eventType === 'Loyalty' || e.eventType === 'Tier' || e.eventType === 'Rewards').length;
    const customerNotes = allActivityEvents.filter(e => e.eventType === 'Customer Notes').length;
    return {
      eventsToday,
      subscriptionChanges,
      paymentEvents,
      paymentFailed,
      loyaltyEvents,
      customerNotes
    };
  }, [allActivityEvents]);
  // Available filters
  const availableFilters: Filter[] = [{
    id: 'timestamp',
    label: 'Timestamp',
    options: [{
      id: '1',
      label: 'Today',
      value: 'today'
    }, {
      id: '2',
      label: 'Last 7 days',
      value: 'last-7-days'
    }, {
      id: '3',
      label: 'Last 30 days',
      value: 'last-30-days'
    }, {
      id: '4',
      label: 'This month',
      value: 'this-month'
    }, {
      id: '5',
      label: 'Custom',
      value: 'custom'
    }],
    selectedValues: filterValues.timestamp || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('timestamp'),
    customDateRange: customDateRanges['timestamp']
  }, {
    id: 'eventType',
    label: 'Event Type',
    options: [{
      id: '1',
      label: 'Subscription',
      value: 'subscription'
    }, {
      id: '2',
      label: 'Order',
      value: 'order'
    }, {
      id: '3',
      label: 'Payment',
      value: 'payment'
    }, {
      id: '4',
      label: 'System',
      value: 'system'
    }, {
      id: '5',
      label: 'Cancellation',
      value: 'cancellation'
    }, {
      id: '6',
      label: 'Settings',
      value: 'settings'
    }, {
      id: '7',
      label: 'Loyalty',
      value: 'loyalty'
    }, {
      id: '8',
      label: 'Tier',
      value: 'tier'
    }, {
      id: '9',
      label: 'Rewards',
      value: 'rewards'
    }, {
      id: '10',
      label: 'Customer Notes',
      value: 'customer notes'
    }, {
      id: '11',
      label: 'Task',
      value: 'task'
    }, {
      id: '12',
      label: 'Automation',
      value: 'automation'
    }],
    selectedValues: filterValues.eventType || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('eventType')
  }, {
    id: 'activity',
    label: 'Activity',
    options: [],
    selectedValue: filterValues.activity?.[0] || '',
    type: 'search',
    isOpen: openFilterDropdowns.has('activity')
  }, {
    id: 'entity',
    label: 'Entity',
    options: [],
    selectedValue: filterValues.entity?.[0] || '',
    type: 'search',
    isOpen: openFilterDropdowns.has('entity')
  }, {
    id: 'source',
    label: 'Source',
    options: [{
      id: '1',
      label: 'Customer Portal',
      value: 'customer-portal'
    }, {
      id: '2',
      label: 'Admin Portal',
      value: 'admin-portal'
    }, {
      id: '3',
      label: 'API',
      value: 'api'
    }, {
      id: '4',
      label: 'Webhook',
      value: 'webhook'
    }, {
      id: '5',
      label: 'System (Automated)',
      value: 'system'
    }],
    selectedValues: filterValues.source || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('source')
  }, {
    id: 'user',
    label: 'User',
    options: [],
    selectedValue: filterValues.user?.[0] || '',
    type: 'search',
    isOpen: openFilterDropdowns.has('user')
  }, {
    id: 'status',
    label: 'Status',
    options: [{
      id: '1',
      label: 'Success',
      value: 'success'
    }, {
      id: '2',
      label: 'Failed',
      value: 'failed'
    }, {
      id: '3',
      label: 'Pending',
      value: 'pending'
    }, {
      id: '4',
      label: 'Warning',
      value: 'warning'
    }],
    selectedValues: filterValues.status || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('status')
  }, {
    id: 'date',
    label: 'Date',
    options: [{
      id: '1',
      label: 'Today',
      value: 'today'
    }, {
      id: '2',
      label: 'Last 7 days',
      value: 'last-7-days'
    }, {
      id: '3',
      label: 'Last 30 days',
      value: 'last-30-days'
    }, {
      id: '4',
      label: 'This month',
      value: 'this-month'
    }, {
      id: '5',
      label: 'Custom',
      value: 'custom'
    }],
    selectedValues: filterValues.date || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('date'),
    customDateRange: customDateRanges['date']
  }];
  // Filter events
  const filteredEvents = useMemo(() => {
    let result = allActivityEvents.filter(event => {
      // Timestamp filter (date range)
      if (filterValues.timestamp && filterValues.timestamp.length > 0) {
        const eventDate = new Date(event.timestamp);
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - eventDate.getTime()) / (1000 * 60 * 60 * 24));
        const timestampMatch = filterValues.timestamp.some(value => {
          if (value === 'today') return daysDiff === 0;
          if (value === 'last-7-days') return daysDiff <= 7;
          if (value === 'last-30-days') return daysDiff <= 30;
          if (value === 'this-month') {
            return eventDate.getMonth() === now.getMonth() && eventDate.getFullYear() === now.getFullYear();
          }
          if (value === 'custom') {
            const dateRange = customDateRanges['timestamp'];
            if (dateRange && dateRange.startDate && dateRange.endDate) {
              const start = new Date(dateRange.startDate);
              const end = new Date(dateRange.endDate);
              end.setHours(23, 59, 59, 999);
              return eventDate >= start && eventDate <= end;
            }
            return true;
          }
          return false;
        });
        if (!timestampMatch) return false;
      }
      // Event type filter
      if (filterValues.eventType && filterValues.eventType.length > 0) {
        const typeMatch = filterValues.eventType.some(value => event.eventType.toLowerCase() === value.toLowerCase());
        if (!typeMatch) return false;
      }
      // Activity filter (search type)
      if (filterValues.activity && filterValues.activity.length > 0 && filterValues.activity[0].trim()) {
        const searchValue = filterValues.activity[0].toLowerCase();
        if (!event.activity.toLowerCase().includes(searchValue)) {
          return false;
        }
      }
      // Entity filter (search type)
      if (filterValues.entity && filterValues.entity.length > 0 && filterValues.entity[0].trim()) {
        const searchValue = filterValues.entity[0].toLowerCase();
        if (!event.entity || !event.entity.toLowerCase().includes(searchValue)) {
          return false;
        }
      }
      // Source filter
      if (filterValues.source && filterValues.source.length > 0) {
        const sourceMatch = filterValues.source.some(value => {
          const sourceValue = event.source.toLowerCase().replace(/\s+/g, '-');
          return sourceValue === value;
        });
        if (!sourceMatch) return false;
      }
      // User filter (search type)
      if (filterValues.user && filterValues.user.length > 0 && filterValues.user[0].trim()) {
        const searchValue = filterValues.user[0].toLowerCase();
        if (!event.user.toLowerCase().includes(searchValue)) {
          return false;
        }
      }
      // Status filter
      if (filterValues.status && filterValues.status.length > 0) {
        const statusMatch = filterValues.status.some(value => event.status.toLowerCase() === value.toLowerCase());
        if (!statusMatch) return false;
      }
      // Date filter
      if (filterValues.date && filterValues.date.length > 0) {
        const eventDate = new Date(event.timestamp);
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - eventDate.getTime()) / (1000 * 60 * 60 * 24));
        const dateMatch = filterValues.date.some(value => {
          if (value === 'today') return daysDiff === 0;
          if (value === 'last-7-days') return daysDiff <= 7;
          if (value === 'last-30-days') return daysDiff <= 30;
          if (value === 'this-month') {
            return eventDate.getMonth() === now.getMonth() && eventDate.getFullYear() === now.getFullYear();
          }
          if (value === 'custom') {
            const dateRange = customDateRanges['date'];
            if (dateRange && dateRange.startDate && dateRange.endDate) {
              const start = new Date(dateRange.startDate);
              const end = new Date(dateRange.endDate);
              end.setHours(23, 59, 59, 999);
              return eventDate >= start && eventDate <= end;
            }
            return true;
          }
          return false;
        });
        if (!dateMatch) return false;
      }
      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        return event.activity.toLowerCase().includes(query) || event.entity?.toLowerCase().includes(query) || event.user.toLowerCase().includes(query) || event.eventType.toLowerCase().includes(query);
      }
      return true;
    });
    // Apply sorting
    if (sortConfig) {
      result = [...result].sort((a, b) => {
        let aValue: any;
        let bValue: any;
        switch (sortConfig.key) {
          case 'timestamp':
            aValue = new Date(a.timestamp).getTime();
            bValue = new Date(b.timestamp).getTime();
            break;
          case 'eventType':
            aValue = a.eventType.toLowerCase();
            bValue = b.eventType.toLowerCase();
            break;
          case 'activity':
            aValue = a.activity.toLowerCase();
            bValue = b.activity.toLowerCase();
            break;
          case 'entity':
            aValue = (a.entity || '').toLowerCase();
            bValue = (b.entity || '').toLowerCase();
            break;
          case 'source':
            aValue = a.source.toLowerCase();
            bValue = b.source.toLowerCase();
            break;
          case 'user':
            aValue = a.user.toLowerCase();
            bValue = b.user.toLowerCase();
            break;
          case 'status':
            aValue = a.status.toLowerCase();
            bValue = b.status.toLowerCase();
            break;
          default:
            return 0;
        }
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [allActivityEvents, filterValues, searchQuery, customDateRanges, sortConfig]);
  // Pagination
  const totalPages = Math.ceil(filteredEvents.length / pageSize);
  const paginatedEvents = filteredEvents.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const hasActiveFilters = addedFilters.size > 0 || searchQuery.trim() !== '';
  // Event type badge variant
  const getEventTypeBadge = (type: string) => {
    const badges: Record<string, {
      bg: string;
      text: string;
      label: string;
    }> = {
      Subscription: {
        bg: '#DBEAFE',
        text: '#1E40AF',
        label: 'Subscription'
      },
      Order: {
        bg: '#EDE9FE',
        text: '#6F42C1',
        label: 'Order'
      },
      Payment: {
        bg: '#FEF3C7',
        text: '#92400E',
        label: 'Payment'
      },
      Cancellation: {
        bg: '#FEE2E2',
        text: '#B91C1C',
        label: 'Cancellation'
      },
      Settings: {
        bg: '#F1F5F9',
        text: '#475569',
        label: 'Settings'
      },
      System: {
        bg: '#E2E8F0',
        text: '#334155',
        label: 'System'
      },
      Loyalty: {
        bg: '#D1FAE5',
        text: '#065F46',
        label: 'Loyalty'
      },
      Tier: {
        bg: '#FFEDD5',
        text: '#9A3412',
        label: 'Tier'
      },
      Rewards: {
        bg: '#FCE7F3',
        text: '#9D174D',
        label: 'Rewards'
      },
      'Customer Notes': {
        bg: '#E0F2FE',
        text: '#0369A1',
        label: 'Customer Notes'
      },
      Task: {
        bg: '#CCFBF1',
        text: '#0F766E',
        label: 'Task'
      },
      Automation: {
        bg: '#EDE9FE',
        text: '#6F42C1',
        label: 'Automation'
      }
    };
    return badges[type] || badges.System;
  };
  // Status badge variant
  const getStatusVariant = (status: string): 'success' | 'error' | 'warning' => {
    if (status === 'Success') return 'success';
    if (status === 'Failed') return 'error';
    return 'warning';
  };
  // Handle filter operations
  const handleClearAll = () => {
    setFilterValues({
      timestamp: [],
      eventType: [],
      activity: [],
      entity: [],
      source: [],
      user: [],
      status: [],
      date: []
    });
    setAddedFilters(new Set());
    setSearchQuery('');
  };
  const handleFilterChange = (filterId: string, value: string | string[]) => {
    setFilterValues({
      ...filterValues,
      [filterId]: Array.isArray(value) ? value : [value]
    });
  };
  const handleFilterRemove = (filterId: string) => {
    const newAddedFilters = new Set(addedFilters);
    newAddedFilters.delete(filterId);
    setAddedFilters(newAddedFilters);
    // Also clear the filter values so the table actually unfilters
    setFilterValues(prev => ({
      ...prev,
      [filterId]: []
    }));
  };
  const handleToggleFilterDropdown = (filterId: string) => {
    const newOpenFilterDropdowns = new Set(openFilterDropdowns);
    newOpenFilterDropdowns.add(filterId);
    setOpenFilterDropdowns(newOpenFilterDropdowns);
  };
  const handleCustomDateChange = (filterId: string, startDate: string, endDate: string) => {
    setCustomDateRanges(prev => ({
      ...prev,
      [filterId]: {
        startDate,
        endDate
      }
    }));
  };
  const handleAddFilter = (filterId: string) => {
    const newAddedFilters = new Set(addedFilters);
    newAddedFilters.add(filterId);
    setAddedFilters(newAddedFilters);
  };
  const handleSort = (columnKey: string) => {
    setSortConfig(current => {
      if (!current || current.key !== columnKey) {
        return {
          key: columnKey,
          direction: 'asc'
        };
      }
      if (current.direction === 'asc') {
        return {
          key: columnKey,
          direction: 'desc'
        };
      }
      return null;
    });
  };
  const renderSortIcon = (columnKey: string) => {
    if (sortConfig?.key === columnKey) {
      return sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-7783" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-7784" />;
    }
    return <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-7785" />;
  };
  const handleRowClick = (event: (typeof allActivityEvents)[0]) => {
    setSelectedEvent(event);
  };
  const handleCloseDrawer = () => {
    setSelectedEvent(null);
    setRawDataExpanded(false);
  };
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-7786">
      {/* NavBar */}
      <div className="mb-6" data-id="element-7787">
        <NavBar tabs={[]} title="Activity Log" subtitle="Track all subscription, loyalty, and system events" data-id="element-7788" />
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8" data-id="element-7789">
        <StatCard label="Events Today" value={stats.eventsToday} secondaryValue="Last event 2 min ago" trend={<TrendIndicator value={12} label="vs yesterday" direction="up" data-id="element-7791" />} data-id="element-7790" />

        <StatCard label="Subscription Changes" value={stats.subscriptionChanges} secondaryValue="Paused, cancelled, updated" data-id="element-7792" />

        <StatCard label="Payment Events" value={stats.paymentEvents} secondaryValue="Processed, failed, recovered" badge={stats.paymentFailed > 0 ? {
        text: `${stats.paymentFailed} Failed`,
        variant: 'error'
      } : undefined} data-id="element-7793" />

        <StatCard label="Loyalty Events" value={stats.loyaltyEvents} secondaryValue="Points, tiers, rewards" data-id="element-7794" />

        <StatCard label="Customer Notes" value={stats.customerNotes} secondaryValue="Notes from team members" data-id="element-7795" />
      </div>

      <hr className="my-10 border-[#CBD5E1]" data-id="element-7796" />

      {/* Filter Bar */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg mb-6 p-6 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-7797">
        <div className="flex flex-col gap-4" data-id="element-7798">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-id="element-7799">
            <div className="w-full lg:w-80 flex-shrink-0" data-id="element-7800">
              <SearchBar placeholder="Search by ID, customer, or action..." value={searchQuery} onChange={setSearchQuery} className="w-full" data-id="element-7801" />
            </div>

            <div className="flex items-center gap-3 flex-shrink-0 ml-auto" data-id="element-7802">
              {hasActiveFilters && <button onClick={handleClearAll} className="inline-flex items-center justify-center h-12 w-12 text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC] border border-[#CBD5E1] rounded-md transition-all duration-200" title="Clear all filters" aria-label="Clear all filters" data-id="element-7803">
                  <X className="w-5 h-5" data-id="element-7804" />
                </button>}

              <ExportButton variant="subscriptions" onExport={async type => {
              console.log(`Exporting ${type}`);
            }} data-id="element-7805" />

              <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} onCustomDateChange={handleCustomDateChange} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyButton={true} data-id="element-7806" />
            </div>
          </div>

          {addedFilters.size > 0 && <div className="w-full" data-id="element-7807">
              <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} onCustomDateChange={handleCustomDateChange} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyChips={true} data-id="element-7808" />
            </div>}
        </div>
      </div>

      {/* Activity Log Table */}
      {filteredEvents.length > 0 ? <>
          <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden mb-6 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-7809">
            <div className="overflow-x-auto" data-id="element-7810">
              <table className="w-full" data-id="element-7811">
                <thead className="bg-[#F1F5F9]" data-id="element-7812">
                  <tr data-id="element-7813">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-40" data-id="element-7814">
                      <button onClick={() => handleSort('timestamp')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-7815">
                        Timestamp
                        {renderSortIcon('timestamp')}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-36" data-id="element-7816">
                      <button onClick={() => handleSort('eventType')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-7817">
                        Event Type
                        {renderSortIcon('eventType')}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-7818">
                      <button onClick={() => handleSort('activity')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-7819">
                        Activity
                        {renderSortIcon('activity')}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-32" data-id="element-7820">
                      <button onClick={() => handleSort('entity')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-7821">
                        Entity
                        {renderSortIcon('entity')}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-32" data-id="element-7822">
                      <button onClick={() => handleSort('source')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-7823">
                        Source
                        {renderSortIcon('source')}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-40" data-id="element-7824">
                      <button onClick={() => handleSort('user')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-7825">
                        User
                        {renderSortIcon('user')}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-28" data-id="element-7826">
                      <button onClick={() => handleSort('status')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-7827">
                        Status
                        {renderSortIcon('status')}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-7828">
                  {paginatedEvents.map(event => {
                const eventBadge = getEventTypeBadge(event.eventType);
                return <tr key={event.id} onClick={() => handleRowClick(event)} className={`hover:bg-[#F8FAFC] transition-colors duration-200 cursor-pointer ${event.status === 'Failed' ? 'border-l-4 border-l-[#EF4444]' : ''}`} style={{
                  height: '64px'
                }} data-id="element-7829">
                        <td className="px-4 py-4" data-id="element-7830">
                          <div className="text-sm text-[#0F172A]" data-id="element-7831">
                            {formatDateTime(event.timestamp)}
                          </div>
                          <div className="text-xs text-[#94A3B8] mt-0.5" data-id="element-7832">
                            {formatTime(event.timestamp)}
                          </div>
                        </td>
                        <td className="px-4 py-4" data-id="element-7833">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap" style={{
                      backgroundColor: eventBadge.bg,
                      color: eventBadge.text
                    }} data-id="element-7834">
                            {eventBadge.label}
                          </span>
                        </td>
                        <td className="px-4 py-4" data-id="element-7835">
                          <span className="text-sm text-[#0F172A]" data-id="element-7836">
                            {event.activity}
                          </span>
                        </td>
                        <td className="px-4 py-4" data-id="element-7837">
                          {event.entity ? <button onClick={e => {
                      e.stopPropagation();
                      if (event.entityType === 'member' || event.entityType === 'bulk-members') {
                        navigate('/loyalty/members');
                      } else if (event.entityType === 'contact') {
                        // Find customer ID by name for navigation
                        const custId = Object.entries(customerNames).find(([_, name]) => name === event.entity)?.[0];
                        if (custId) navigate(`/contacts/${custId}`);
                      } else {
                        console.log(`Navigate to ${event.entity}`);
                      }
                    }} className="text-sm text-[#0EA5E9] hover:text-[#0284C7] hover:underline font-medium transition-colors" data-id="element-7838">
                              {event.entity}
                            </button> : <span className="text-sm text-[#94A3B8]" data-id="element-7839">—</span>}
                        </td>
                        <td className="px-4 py-4" data-id="element-7840">
                          <span className="text-sm text-[#475569]" data-id="element-7841">
                            {event.source}
                          </span>
                        </td>
                        <td className="px-4 py-4" data-id="element-7842">
                          <span className="text-sm text-[#475569]" data-id="element-7843">
                            {event.user}
                          </span>
                        </td>
                        <td className="px-4 py-4" data-id="element-7844">
                          <Badge variant={getStatusVariant(event.status)} data-id="element-7845">
                            {event.status}
                          </Badge>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between" data-id="element-7846">
            <div className="text-[13px] text-[#64748B]" data-id="element-7847">
              Showing {(currentPage - 1) * pageSize + 1}-
              {Math.min(currentPage * pageSize, filteredEvents.length)} of{' '}
              {filteredEvents.length} events
            </div>
            <div className="flex items-center gap-2" data-id="element-7848">
              <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200" data-id="element-7849">
                Previous
              </button>
              <div className="flex items-center gap-1" data-id="element-7850">
                {Array.from({
              length: Math.min(totalPages, 5)
            }, (_, i) => {
              const page = i + 1;
              return <button key={page} onClick={() => setCurrentPage(page)} className={`w-10 h-10 rounded-md text-[13px] font-medium transition-colors duration-200 ${page === currentPage ? 'bg-[#0EA5E9] text-white' : 'text-[#475569] hover:bg-[#F8FAFC]'}`} data-id="element-7851">
                        {page}
                      </button>;
            })}
              </div>
              <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200" data-id="element-7852">
                Next
              </button>
            </div>
          </div>
        </> : <div className="bg-white rounded-lg border border-[#E2E8F0] p-12 text-center shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-7853">
          <ClipboardList className="w-16 h-16 text-[#CBD5E1] mx-auto mb-4" data-id="element-7854" />
          <h3 className="text-xl font-semibold text-[#0F172A] mb-2" data-id="element-7855">
            No events found
          </h3>
          <p className="text-[#64748B] mb-6" data-id="element-7856">
            Try adjusting your filters or date range
          </p>
          {hasActiveFilters && <Button variant="outline" onClick={handleClearAll} data-id="element-7857">
              Clear filters
            </Button>}
        </div>}

      {/* Event Detail Drawer */}
      {selectedEvent && <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={handleCloseDrawer} data-id="element-7858" />
          <div className="fixed right-0 top-0 bottom-0 w-[400px] bg-white shadow-xl z-50 overflow-y-auto" data-id="element-7859">
            {/* Header */}
            <div className="p-6 border-b border-[#E2E8F0]" data-id="element-7860">
              <div className="flex items-start justify-between mb-3" data-id="element-7861">
                <div className="flex-1" data-id="element-7862">
                  {(() => {
                const badge = getEventTypeBadge(selectedEvent.eventType);
                return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium mb-2" style={{
                  backgroundColor: badge.bg,
                  color: badge.text
                }} data-id="element-7863">
                        {badge.label}
                      </span>;
              })()}
                  <h2 className="text-[18px] font-semibold text-[#0F172A] mt-2" data-id="element-7864">
                    {selectedEvent.activity.split(' — ')[0]}
                  </h2>
                </div>
                <button onClick={handleCloseDrawer} className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-7865">
                  <X className="w-5 h-5 text-[#64748B]" data-id="element-7866" />
                </button>
              </div>
              <p className="text-[13px] text-[#64748B]" data-id="element-7867">
                {formatDateTime(selectedEvent.timestamp)} ·{' '}
                {formatTime(selectedEvent.timestamp)}
              </p>
            </div>

            {/* Note Content - Show for Customer Notes */}
            {selectedEvent.eventType === 'Customer Notes' && <div className="p-6 border-b border-[#E2E8F0]" data-id="element-7868">
                <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-7869">
                  Note Content
                </h3>
                <div className="p-3 bg-[#F8FAFC] rounded-md border border-[#E2E8F0]" data-id="element-7870">
                  <p className="text-[14px] text-[#475569] italic" data-id="element-7871">
                    "
                    {selectedEvent.activity.split(' — "')[1]?.replace('"', '') || selectedEvent.activity}
                    "
                  </p>
                </div>
              </div>}

            {/* Details Section */}
            <div className="p-6 border-b border-[#E2E8F0]" data-id="element-7872">
              <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-7873">
                Details
              </h3>
              <div className="space-y-3" data-id="element-7874">
                {selectedEvent.entity && <div data-id="element-7875">
                    <div className="text-[12px] text-[#94A3B8] mb-1" data-id="element-7876">
                      Entity
                    </div>
                    <button className="text-[14px] text-[#0EA5E9] hover:text-[#0284C7] hover:underline font-medium" data-id="element-7877">
                      {selectedEvent.entity}
                    </button>
                  </div>}
                <div data-id="element-7878">
                  <div className="text-[12px] text-[#94A3B8] mb-1" data-id="element-7879">Source</div>
                  <div className="text-[14px] text-[#0F172A]" data-id="element-7880">
                    {selectedEvent.source}
                  </div>
                </div>
                <div data-id="element-7881">
                  <div className="text-[12px] text-[#94A3B8] mb-1" data-id="element-7882">User</div>
                  <div className="text-[14px] text-[#0F172A]" data-id="element-7883">
                    {selectedEvent.user}
                  </div>
                </div>
                <div data-id="element-7884">
                  <div className="text-[12px] text-[#94A3B8] mb-1" data-id="element-7885">Status</div>
                  <Badge variant={getStatusVariant(selectedEvent.status)} data-id="element-7886">
                    {selectedEvent.status}
                  </Badge>
                </div>
                {selectedEvent.ipAddress && <div data-id="element-7887">
                    <div className="text-[12px] text-[#94A3B8] mb-1" data-id="element-7888">
                      IP Address
                    </div>
                    <div className="text-[14px] text-[#0F172A] font-mono" data-id="element-7889">
                      {selectedEvent.ipAddress}
                    </div>
                  </div>}
              </div>
            </div>

            {/* Changes Section */}
            {selectedEvent.changes.length > 0 && <div className="p-6 border-b border-[#E2E8F0]" data-id="element-7890">
                <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-7891">
                  Changes
                </h3>
                <div className="space-y-3" data-id="element-7892">
                  {selectedEvent.changes.map((change, index) => <div key={index} className="p-3 bg-[#F8FAFC] rounded-md" data-id="element-7893">
                      <div className="text-[12px] font-medium text-[#64748B] mb-2" data-id="element-7894">
                        {change.field}
                      </div>
                      <div className="flex items-center gap-2" data-id="element-7895">
                        <span className="text-[14px] text-[#EF4444] line-through" data-id="element-7896">
                          {change.before}
                        </span>
                        <span className="text-[#94A3B8]" data-id="element-7897">→</span>
                        <span className="text-[14px] text-[#10B981] font-medium" data-id="element-7898">
                          {change.after}
                        </span>
                      </div>
                    </div>)}
                </div>
              </div>}

            {/* Related Events */}
            {selectedEvent.entity && <div className="p-6 border-b border-[#E2E8F0]" data-id="element-7899">
                <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-7900">
                  Related Events
                </h3>
                <button className="text-[14px] text-[#0EA5E9] hover:text-[#0284C7] hover:underline font-medium" data-id="element-7901">
                  View all events for {selectedEvent.entity} →
                </button>
              </div>}

            {/* Raw Data */}
            <div className="p-6" data-id="element-7902">
              <button onClick={() => setRawDataExpanded(!rawDataExpanded)} className="flex items-center justify-between w-full mb-3" data-id="element-7903">
                <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider" data-id="element-7904">
                  Raw Data
                </h3>
                {rawDataExpanded ? <ChevronUp className="w-5 h-5 text-[#64748B]" data-id="element-7905" /> : <ChevronDown className="w-5 h-5 text-[#64748B]" data-id="element-7906" />}
              </button>
              {rawDataExpanded && <div className="relative" data-id="element-7907">
                  <pre className="p-3 bg-[#F8FAFC] rounded-md text-[12px] text-[#0F172A] overflow-x-auto font-mono" data-id="element-7908">
                    {JSON.stringify(selectedEvent, null, 2)}
                  </pre>
                  <button onClick={() => copyToClipboard(JSON.stringify(selectedEvent, null, 2))} className="absolute top-2 right-2 p-2 bg-white border border-[#CBD5E1] rounded-md hover:bg-[#F1F5F9] transition-colors" title="Copy to clipboard" data-id="element-7909">
                    <Copy className="w-4 h-4 text-[#64748B]" data-id="element-7910" />
                  </button>
                </div>}
            </div>
          </div>
        </>}
    </div>;
}